import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Save,
  X,
  Plus,
  Minus,
  Eye,
  EyeOff,
  Hash,
  Type,
  Calendar,
  ToggleLeft,
  ToggleRight,
  FileText,
  AlertCircle,
  Check,
  Sparkles,
  Cpu,
  Image as ImageIcon,
  Music,
  Video,
  File
} from 'lucide-react';
import { useMediaRecord } from '../../hooks/useMediaRecord';
import {
  MediaRecord,
  MediaRecordUpdateForm,
  ExtractedMetadata,
  AIEnhancedMetadata,
  MediaType
} from '../../types/mediaRecord';

interface MetadataEditorProps {
  mediaId: string;
  className?: string;
}

type MetadataFieldType = 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';

interface MetadataField {
  key: string;
  value: any;
  type: MetadataFieldType;
  isEditing: boolean;
  isNew?: boolean;
}

const detectFieldType = (value: any): MetadataFieldType => {
  if (Array.isArray(value)) return 'array';
  if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) return 'date';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'object' && value !== null) return 'object';
  return 'string';
};

const getFieldIcon = (type: MetadataFieldType) => {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'boolean': return ToggleLeft;
    case 'array': return FileText;
    case 'object': return FileText;
    case 'date': return Calendar;
    default: return Type;
  }
};

const formatValue = (value: any, type: MetadataFieldType): string => {
  if (value === null || value === undefined) return '';
  
  switch (type) {
    case 'array':
      return Array.isArray(value) ? value.join(', ') : String(value);
    case 'object':
      return JSON.stringify(value, null, 2);
    case 'date':
      if (value instanceof Date) return value.toISOString().split('T')[0];
      if (typeof value === 'string') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? value : date.toISOString().split('T')[0];
      }
      return String(value);
    case 'boolean':
      return Boolean(value).toString();
    default:
      return String(value);
  }
};

const parseValue = (value: string, type: MetadataFieldType): any => {
  if (!value.trim()) return null;
  
  switch (type) {
    case 'number':
      const num = parseFloat(value);
      return isNaN(num) ? null : num;
    case 'boolean':
      return value.toLowerCase() === 'true';
    case 'array':
      return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    case 'object':
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    case 'date':
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    default:
      return value;
  }
};

interface FieldEditorProps {
  field: MetadataField;
  onSave: (key: string, value: any, type: MetadataFieldType) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({ field, onSave, onCancel, onDelete }) => {
  const [editKey, setEditKey] = useState(field.key);
  const [editValue, setEditValue] = useState(formatValue(field.value, field.type));
  const [editType, setEditType] = useState(field.type);
  const [showValue, setShowValue] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const IconComponent = getFieldIcon(editType);

  const handleSave = () => {
    if (!editKey.trim()) {
      setError('Key is required');
      return;
    }

    const parsedValue = parseValue(editValue, editType);
    if (editValue.trim() && parsedValue === null && editType !== 'string') {
      setError(`Invalid ${editType} value`);
      return;
    }

    onSave(editKey, parsedValue, editType);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="cosmic-glass rounded-lg p-4 border-2 border-blue-400/50"
    >
      <div className="space-y-4">
        {/* Key and Type */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Key</label>
            <input
              type="text"
              value={editKey}
              onChange={(e) => {
                setEditKey(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyPress}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors"
              placeholder="metadata_key"
              autoFocus={field.isNew}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
            <select
              value={editType}
              onChange={(e) => setEditType(e.target.value as MetadataFieldType)}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white outline-none focus:border-blue-400 transition-colors"
            >
              <option value="string">String</option>
              <option value="number">Number</option>
              <option value="boolean">Boolean</option>
              <option value="array">Array</option>
              <option value="object">Object</option>
              <option value="date">Date</option>
            </select>
          </div>
        </div>

        {/* Value */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="text-sm font-medium text-gray-300">Value</label>
            <IconComponent className="w-4 h-4 text-gray-400" />
            {(editType === 'object' || editType === 'array') && (
              <button
                onClick={() => setShowValue(!showValue)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            )}
          </div>
          
          {editType === 'boolean' ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setEditValue('true')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  editValue === 'true' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <ToggleRight className="w-4 h-4" />
                True
              </button>
              <button
                onClick={() => setEditValue('false')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  editValue === 'false' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-white/5 text-gray-400 hover:text-white'
                }`}
              >
                <ToggleLeft className="w-4 h-4" />
                False
              </button>
            </div>
          ) : editType === 'object' || (editType === 'array' && showValue) ? (
            <textarea
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyPress}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors font-mono text-sm resize-none"
              rows={editType === 'object' ? 6 : 3}
              placeholder={editType === 'object' ? '{"key": "value"}' : 'item1, item2, item3'}
            />
          ) : (
            <input
              type={editType === 'number' ? 'number' : editType === 'date' ? 'date' : 'text'}
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyPress}
              className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-400 outline-none focus:border-blue-400 transition-colors"
              placeholder={`Enter ${editType} value...`}
            />
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
          <button
            onClick={onCancel}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
          {onDelete && !field.isNew && (
            <button
              onClick={onDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ml-auto"
            >
              <Minus className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

interface MetadataFieldDisplayProps {
  field: MetadataField;
  onEdit: () => void;
  onDelete: () => void;
}

const MetadataFieldDisplay: React.FC<MetadataFieldDisplayProps> = ({ field, onEdit, onDelete }) => {
  const [showFullValue, setShowFullValue] = useState(false);
  const IconComponent = getFieldIcon(field.type);
  
  const displayValue = formatValue(field.value, field.type);
  const isLongValue = displayValue.length > 100;
  const truncatedValue = isLongValue && !showFullValue 
    ? displayValue.substring(0, 100) + '...' 
    : displayValue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="cosmic-glass rounded-lg p-4 group hover:bg-white/10 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <IconComponent className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="font-medium text-white">{field.key}</span>
            <span className="text-xs px-2 py-1 bg-white/10 text-gray-400 rounded-full">
              {field.type}
            </span>
          </div>
          
          <div className="text-sm text-gray-300">
            {field.type === 'object' || field.type === 'array' ? (
              <pre className="whitespace-pre-wrap font-mono text-xs bg-white/5 p-2 rounded">
                {truncatedValue}
              </pre>
            ) : (
              <span className="break-words">{truncatedValue}</span>
            )}
            
            {isLongValue && (
              <button
                onClick={() => setShowFullValue(!showFullValue)}
                className="text-blue-400 hover:text-blue-300 text-xs ml-2 transition-colors"
              >
                {showFullValue ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-2 text-gray-400 hover:text-blue-400 rounded transition-colors"
              title="Edit field"
            >
              <Type className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-400 rounded transition-colors"
              title="Delete field"
            >
              <Minus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const MetadataEditor: React.FC<MetadataEditorProps> = ({ mediaId, className }) => {
  const { data: record, update, isUpdating } = useMediaRecord(mediaId);
  const [fields, setFields] = useState<MetadataField[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Initialize fields from record metadata
  useEffect(() => {
    if (record?.metadata) {
      const metadataFields: MetadataField[] = Object.entries(record.metadata).map(([key, value]) => ({
        key,
        value,
        type: detectFieldType(value),
        isEditing: false
      }));
      setFields(metadataFields);
      setHasChanges(false);
    }
  }, [record]);

  const handleAddField = () => {
    const newField: MetadataField = {
      key: '',
      value: '',
      type: 'string',
      isEditing: true,
      isNew: true
    };
    setFields(prev => [newField, ...prev]);
  };

  const handleSaveField = (oldKey: string, newKey: string, value: any, type: MetadataFieldType) => {
    setFields(prev => {
      const updated = prev.map(field => {
        if (field.key === oldKey || (field.isNew && field.key === '')) {
          return {
            key: newKey,
            value,
            type,
            isEditing: false,
            isNew: false
          };
        }
        return field;
      });
      
      // Remove duplicates if key changed
      if (oldKey !== newKey) {
        return updated.filter((field, index, arr) => 
          arr.findIndex(f => f.key === field.key) === index
        );
      }
      
      return updated;
    });
    setHasChanges(true);
  };

  const handleCancelEdit = (key: string) => {
    setFields(prev => {
      if (key === '' || prev.find(f => f.key === key)?.isNew) {
        return prev.filter(f => !(f.key === key || (f.isNew && f.key === '')));
      }
      return prev.map(field => 
        field.key === key ? { ...field, isEditing: false } : field
      );
    });
  };

  const handleEditField = (key: string) => {
    setFields(prev => prev.map(field => 
      field.key === key ? { ...field, isEditing: true } : field
    ));
  };

  const handleDeleteField = (key: string) => {
    if (confirm(`Are you sure you want to delete the "${key}" field?`)) {
      setFields(prev => prev.filter(field => field.key !== key));
      setHasChanges(true);
    }
  };

  const handleSaveAll = async () => {
    if (!hasChanges || !record) return;

    setIsSaving(true);
    try {
      const newMetadata = fields.reduce((acc, field) => {
        if (field.key && !field.isEditing) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {} as Record<string, any>);

      await update({ metadata: newMetadata });
      setHasChanges(false);
      setSaveMessage('Metadata saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save metadata:', error);
      setSaveMessage('Failed to save metadata');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetChanges = () => {
    if (record?.metadata) {
      const metadataFields: MetadataField[] = Object.entries(record.metadata).map(([key, value]) => ({
        key,
        value,
        type: detectFieldType(value),
        isEditing: false
      }));
      setFields(metadataFields);
      setHasChanges(false);
    }
  };

  const getMediaTypeIcon = (mediaType: MediaType) => {
    switch (mediaType) {
      case 'video': return Video;
      case 'audio': return Music;
      case 'image': return ImageIcon;
      default: return File;
    }
  };

  if (!record) {
    return (
      <div className={`cosmic-glass rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-white/10 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const MediaIcon = getMediaTypeIcon(record.media_type);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`cosmic-glass rounded-lg overflow-hidden ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MediaIcon className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Metadata Editor</h2>
            <span className="px-2 py-1 bg-blue-400/20 text-blue-300 text-xs rounded-full">
              {fields.length} fields
            </span>
          </div>

          <div className="flex items-center gap-3">
            {saveMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                  saveMessage.includes('success') 
                    ? 'bg-green-400/20 text-green-300' 
                    : 'bg-red-400/20 text-red-300'
                }`}
              >
                <Check className="w-4 h-4" />
                {saveMessage}
              </motion.div>
            )}

            <button
              onClick={handleAddField}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Field
            </button>

            {hasChanges && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResetChanges}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={handleSaveAll}
                  disabled={isSaving || isUpdating}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isSaving || isUpdating ? 'Saving...' : 'Save All'}
                </button>
              </div>
            )}
          </div>
        </div>

        {hasChanges && (
          <div className="mt-3 p-3 bg-yellow-400/10 border border-yellow-400/20 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-300">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">You have unsaved changes</span>
            </div>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="p-6">
        {fields.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No metadata fields</p>
            <p className="text-sm opacity-75">Add your first metadata field to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {fields.map((field, index) => (
                <div key={`${field.key}-${index}`}>
                  {field.isEditing ? (
                    <FieldEditor
                      field={field}
                      onSave={(key, value, type) => handleSaveField(field.key, key, value, type)}
                      onCancel={() => handleCancelEdit(field.key)}
                      onDelete={field.isNew ? undefined : () => handleDeleteField(field.key)}
                    />
                  ) : (
                    <MetadataFieldDisplay
                      field={field}
                      onEdit={() => handleEditField(field.key)}
                      onDelete={() => handleDeleteField(field.key)}
                    />
                  )}
                </div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* AI Enhancement */}
      <div className="p-6 border-t border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <div>
              <h3 className="font-medium text-white">AI Enhancement</h3>
              <p className="text-sm text-gray-400">Let AI suggest and extract metadata</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
              <Cpu className="w-4 h-4" />
              Extract Metadata
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">
              <Sparkles className="w-4 h-4" />
              AI Enhance
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MetadataEditor;